// See below -- required for the classic JSX transform this app's
// tsconfig uses (react/jsx-runtime can't resolve once this bundle treats
// react as a federation-shared external).
import * as React from 'react';
import { useEffect, useState } from 'react';
import { CLAIM_EI, getStoredSession, hasClaim, onSessionChange } from '@accenturecodefoundry/mfe-pot-shared-auth/core';
import type { ContentClient, PageContent } from '@accenturecodefoundry/mfe-pot-shared-content-client';
import { useLocale, useTranslations } from '@accenturecodefoundry/mfe-pot-shared-i18n';
import type { EiClaim, EmploymentInsuranceApiClient } from 'employment-insurance-data-access';
import { HttpEmploymentInsuranceApiClient } from 'employment-insurance-data-access';
import { createContentClient, INTRO_CONTENT_KEY } from './content-client';
import { loadRuntimeConfig } from '../runtime-config';
import { assetBaseUrl } from './asset-base-url';
import { Applications } from './Applications';
import { ReportingOverview } from './ReportingOverview';
import './register-scds';
import './App.css';

interface RemoteConfig {
  apiClient: EmploymentInsuranceApiClient;
  contentClient: ContentClient;
}

/**
 * Does its own setup entirely -- no host-provided REMOTE_PROVIDERS
 * equivalent (see RemoteRouteHost in shared-federation-runtime for why).
 * Auth/claim check lives here, not in the feature components -- defense
 * in depth, this app validates its own claim independently.
 *
 * **One mode at a time** (docs/specs/015-ei-application-reporting-mode-switch.md):
 * the page shows either the application flow or the reporting flow,
 * decided by whether the citizen has a claim on file (`getClaim`, resolved
 * once per session). No claim -> apply mode (`Applications`); a claim ->
 * report mode (spec 016's `ReportingOverview`: the EI Reporting screen with
 * its own heading, in place of the CMS intro). A failed lookup
 * falls back to apply mode, so a new citizen is never stranded; while the
 * lookup is unresolved only the intro renders. Claim existence is the only
 * gate -- claim-status nuance (denied/closed) is deferred.
 *
 * After a successful submit, `Applications` calls `onClaimCreated` with
 * the new claim and the page flips to report mode without a reload. In
 * that one case `Applications` stays mounted above the reporting sections,
 * so its own confirmation card stays visible until the next page load; a
 * fresh load with a claim shows report mode only.
 *
 * The overview's demo "end claim" control (spec 016) calls back here: the
 * claim is cleared and the page returns to apply mode with a *fresh* wizard
 * (a new key remounts Applications, so any earlier confirmation is gone),
 * without a reload.
 *
 * `ReportingStatus.tsx` no longer renders on this page (spec 016: the
 * reporting screen's banner and table carry that information). It stays
 * exported as the federated `./EiReportingStatusWidget` for the dashboard
 * and life-events, fully self-configuring as before.
 */
export function App() {
  const [hasAccess, setHasAccess] = useState(() => hasClaim(getStoredSession(), CLAIM_EI));
  const [config, setConfig] = useState<RemoteConfig | null>(null);
  const [intro, setIntro] = useState<PageContent | null>(null);
  const [introLoadError, setIntroLoadError] = useState(false);
  // Spec 015's mode switch: the claim on file (null = none), whether the
  // lookup has finished, and whether the claim was created on this page.
  const [claim, setClaim] = useState<EiClaim | null>(null);
  const [claimResolved, setClaimResolved] = useState(false);
  const [justApplied, setJustApplied] = useState(false);
  // Bumped when the demo end-claim returns the page to apply mode, so
  // Applications remounts as a fresh wizard.
  const [wizardKey, setWizardKey] = useState(0);
  const locale = useLocale();
  const { t } = useTranslations(assetBaseUrl, locale);

  useEffect(() => onSessionChange((session) => setHasAccess(hasClaim(session, CLAIM_EI))), []);

  useEffect(() => {
    let cancelled = false;
    loadRuntimeConfig(assetBaseUrl).then((runtimeConfig) => {
      if (cancelled) {
        return;
      }
      setConfig({
        apiClient: new HttpEmploymentInsuranceApiClient(runtimeConfig.employmentInsuranceBffBaseUrl),
        contentClient: createContentClient(runtimeConfig.strapiBaseUrl, assetBaseUrl),
      });
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // CMS content (unlike the i18n chrome text above) isn't reactive by
  // default, so this app explicitly re-fetches whenever the cross-remote
  // active locale changes -- same pattern as every other converted app.
  useEffect(() => {
    if (!config) {
      return;
    }
    let cancelled = false;
    config.contentClient
      .getPageContent(INTRO_CONTENT_KEY, locale === 'fr' ? 'fr' : 'en')
      .then((content) => {
        if (!cancelled) {
          setIntro(content);
          setIntroLoadError(false);
        }
      })
      .catch((err) => {
        console.error('Failed to load employment-insurance intro content', err);
        if (!cancelled) {
          setIntroLoadError(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [config, locale]);

  // Resolve the claim once the config and the session are both available.
  // `getClaim` already maps a 404 to null; any other failure (BFF
  // unreachable) is logged and leaves the claim null, i.e. apply mode --
  // failing closed to report mode would strand a citizen who has no claim.
  useEffect(() => {
    if (!config || !hasAccess) {
      return;
    }
    const session = getStoredSession();
    if (!session) {
      return;
    }
    let cancelled = false;
    config.apiClient
      .getClaim(session.sub)
      .then((found) => {
        if (!cancelled) {
          setClaim(found);
        }
      })
      .catch((err) => {
        console.error('Failed to look up the EI claim; showing the application flow', err);
      })
      .finally(() => {
        if (!cancelled) {
          setClaimResolved(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [config, hasAccess]);

  function handleClaimCreated(created: EiClaim): void {
    setJustApplied(true);
    setClaim(created);
  }

  function handleClaimEnded(): void {
    setClaim(null);
    setJustApplied(false);
    setWizardKey((key) => key + 1);
  }

  if (!hasAccess) {
    return <p role="alert">{t('auth.signInRequired')}</p>;
  }

  if (!config) {
    return null;
  }

  const mode: 'apply' | 'report' = claim ? 'report' : 'apply';

  return (
    <div className="ei-page scds-container--narrow">
      {/* The CMS intro belongs to apply mode (and the brief unresolved
          moment); report mode has its own heading inside ReportingOverview. */}
      {mode === 'report' && claimResolved ? null : intro ? (
        <section>
          {/* scds-section-heading, not a plain <h1> -- adds the Canada.ca H1
              red bar, matching msca-shell's own page titles (e.g. the Life
              Events hub's "What's happening in your life?"). */}
          <scds-section-heading heading={intro.title} heading-tag="h1" />
          <p>{intro.body}</p>
        </section>
      ) : introLoadError ? (
        <p role="alert">{t('errors.contentUnavailable')}</p>
      ) : null}
      {/* Nothing below the intro until the claim lookup resolves. Applications
          stays at this same position when the page flips to report mode
          after a submit, so React keeps it mounted and its confirmation
          card survives the switch. */}
      {claimResolved && (mode === 'apply' || justApplied) ? (
        <Applications
          key={wizardKey}
          apiClient={config.apiClient}
          contentClient={config.contentClient}
          locale={locale}
          onClaimCreated={handleClaimCreated}
        />
      ) : null}
      {claimResolved && mode === 'report' ? (
        <ReportingOverview
          apiClient={config.apiClient}
          contentClient={config.contentClient}
          locale={locale}
          onClaimEnded={handleClaimEnded}
        />
      ) : null}
    </div>
  );
}
